<script>
	import "./TimeSlider.scss";
	import { onMount, onDestroy, afterUpdate } from "svelte";
	import { get } from "svelte/store";
	import Button, { Group, GroupItem, Label, Icon as ButtonIcon } from "@smui/button";
	import Fab, { Label as FabLabel, Icon as FabIcon } from "@smui/fab";
	import MenuItem from "../FlyoutMenu/MenuItem.svelte";
	import moment from "moment";
	import TimeSliderData from "./TimeSliderData";
	import TimeSliderProgressMode from "./TimeSliderProgressMode";

	// TimeSliderData structure passed in.
	// We bind this component to the "instance" property.
	// We keep the data separate so we can access it from elsewhere in the app before the component is mounted/etc.
	export let data = null;
	if (data === null) {
		data = new TimeSliderData();
	}
	data.instance = this;

	export let hasNowButton = false;

	// Local refs to TimeSliderData writables.
	let value = data.value;
	let valueMin = data.min;
	let valueMax = data.max;
	let eachIncrement = data.step; // 5 minutes per step increment

	let datetimeString;
	let slider;
	let playButton;

	// Default 7 days.
	let numberOfIncrements = 86400 * 7 / $eachIncrement;

	export const setValue = (val) => {
		$value = val;
	}
	export const setMinMax = (min, max) => {
		$valueMin = min;
		$valueMax = max;
	};

	const onSliderValueChanged = () => {
		if (slider) {
			let result = moment($valueMin).add(slider.value * $eachIncrement, "seconds");
			$value = result;
		}
	};
	const updateBounds = () => {
		if (slider) {
			const secondsInTimePeriod = Math.floor($valueMax.diff($valueMin) / 1000);
			numberOfIncrements = secondsInTimePeriod / $eachIncrement;
			// console.log("secondsInTimePeriod", secondsInTimePeriod, "numberOfIncrements", numberOfIncrements);
		}
	};
	const displayDateTime = value => {
		datetimeString = moment.utc(value).format("MMMM Do YYYY, hh:mm:ssa [(UTC)]");
		if (slider) {
			const breadth = $valueMax.diff($valueMin);
			const difference = $valueMin.diff(moment(value));
			const v = Math.floor(difference / ($eachIncrement*1000)) * -1;
			slider.value = v;
		}
	};
	$: {
		$value;
		$valueMin;
		$valueMax;
		$eachIncrement;
		//onSliderValueChanged();
	}
	$: {
		displayDateTime($value);
	}
	$: {
		$valueMin;
		$valueMax;
		$eachIncrement;
		updateBounds();
	}
	onMount(() => {
		updateBounds();
	});

	/**
	 * @param amount positive or negative amount to move
	 * @param unit 'second', 'minute', 'hour', 'day'  (defined by moment.js)
	 * @return true if a valid adjustment has been made, false if not
	 */
	const nudgeSnapshot = (amount, unit) => {
		const currentValue = moment($value);
		const nextValue = currentValue.add(amount, unit);
		if (nextValue > $valueMax) {
			$value = $valueMax;
			return false;
		} else if (nextValue < $valueMin) {
			$value = $valueMin;
			return false;
		} else {
			$value = nextValue;
			return true;
		}
	};
	let canSetToCurrentTime;
	$: {
		const t = moment();
		canSetToCurrentTime = (t >= $valueMin && t <= $valueMax);
	}
	const setToCurrentTime = () => {
		if (canSetToCurrentTime) {
			$value = moment();
			afterUpdate(() => {
				onSliderValueChanged();
			});
		}
	};

	const jumpToEnd = minOrMax => {
		if (minOrMax === "min") {
			$value = $valueMin;
		} else if (minOrMax === "max") {
			$value = $valueMax;
		} else {
			console.warn("Invalid parameter passed to jumpToEnd", minOrMax);
		}
	};

	let autoProgressMode = TimeSliderProgressMode.STOPPED;
	let autoProgressInterval;
	let progressSpeed = {
		[TimeSliderProgressMode.STOPPED]: 0,
		[TimeSliderProgressMode.PLAYING]: 20,
		[TimeSliderProgressMode.FASTFORWARD]: 60
	};
	const stepAutoProgressModes = (reset = false) => {
		clearInterval(autoProgressInterval);

		if (reset || autoProgressMode === TimeSliderProgressMode.FASTFORWARD) {
			autoProgressMode = TimeSliderProgressMode.STOPPED;
		}
		else if (autoProgressMode === TimeSliderProgressMode.PLAYING) {
			autoProgressMode = TimeSliderProgressMode.FASTFORWARD;
		}
		else if (autoProgressMode === TimeSliderProgressMode.STOPPED) {
			autoProgressMode = TimeSliderProgressMode.PLAYING;
		}

		if (autoProgressMode !== TimeSliderProgressMode.STOPPED) {
			//If at max, set date to min before progressing
			if (autoProgressMode === TimeSliderProgressMode.PLAYING && slider.value >= numberOfIncrements) { //at max
				$value = $valueMin;
			}

			autoProgressInterval = window.setInterval(() => {
				const success = nudgeSnapshot(progressSpeed[autoProgressMode], "minutes");
				//Clear when it gets to the end
				if (!success) {
					stepAutoProgressModes(true);
				}
			}, 500);
		}
	};

	onDestroy(() => {
		clearInterval(autoProgressInterval);
	});
</script>

<MenuItem title={"Timeline"} materialIcon={"query_builder"} class="timeslider-card">

	<div class="timeline-controls">
		<Fab class="min-button" on:click={() => jumpToEnd("min")} color="secondary" mini>
			<FabIcon class="material-icons">skip_previous</FabIcon>
		</Fab>
		{#if autoProgressMode === TimeSliderProgressMode.PLAYING}
			<Fab class="play-button" on:click={() => stepAutoProgressModes()} color="secondary" mini>
				<FabIcon bind:this={playButton} class="material-icons auto-progress-icon">
					fast_forward
				</FabIcon>
			</Fab>
		{:else if autoProgressMode === TimeSliderProgressMode.FASTFORWARD}
			<Fab class="play-button" on:click={() => stepAutoProgressModes()} color="secondary" mini>
				<FabIcon bind:this={playButton} class="material-icons auto-progress-icon">
					stop
				</FabIcon>
			</Fab>
		{:else}
			<Fab class="play-button" on:click={() => stepAutoProgressModes()} color="secondary" mini>
				<FabIcon bind:this={playButton} class="material-icons auto-progress-icon">
					play_arrow
				</FabIcon>
			</Fab>
		{/if}
		<Fab class="max-button" on:click={() => jumpToEnd("max")} color="secondary" mini>
			<FabIcon class="material-icons">skip_next</FabIcon>
		</Fab>
	</div>
	<input bind:this={slider} type="range" class="slider" name="date"
		min="0"
		max={numberOfIncrements}
		value={numberOfIncrements}
		on:input={e => onSliderValueChanged(e.target.value)} />

	<div class="date-container mdc-elevation--z2">
		<Group class="nudge-button-group top" variant="unelevated">
			<Button class='bg-info' color="secondary" variant="unelevated" on:click={() => nudgeSnapshot(1, 'day')}><Label>+1 Day</Label></Button>
			<Button class='bg-info' color="secondary" variant="unelevated" on:click={() => nudgeSnapshot(1, 'hour')}><Label>+1 Hour</Label></Button>
			<Button class='bg-info' color="secondary" variant="unelevated" on:click={() => nudgeSnapshot(5, 'minute')}><Label>+5 Mins</Label>
			</Button>
		</Group>
		<div class="date"><p>{datetimeString}</p></div>
		<Group class="nudge-button-group bottom" variant="unelevated">
			<Button class='bg-info' color="secondary" variant="unelevated" on:click={() => nudgeSnapshot(-1, 'day')}><Label>-1 Day</Label></Button>
			<Button class='bg-info' color="secondary" variant="unelevated" on:click={() => nudgeSnapshot(-1, 'hour')}><Label>-1 Hour</Label>
			</Button>
			<Button class='bg-info' color="secondary" variant="unelevated" on:click={() => nudgeSnapshot(-5, 'minute')}><Label>-5 Mins</Label>
			</Button>
		</Group>
	</div>
	{#if hasNowButton && canSetToCurrentTime}
		<Button class='bg-info' color="secondary" variant="unelevated" on:click={() => { setToCurrentTime(); }}>
			<Label>Set to current time</Label>
		</Button>
	{/if}

</MenuItem>
